<template>
	<BaseAlert v-if="!domains || domains.filter(d => d.active).length === 0" type="warn">
		Agent doesn't have any active domains
	</BaseAlert>
	<BaseAlert v-else-if="domainRenderAttempts.length === 0" type="warn">
		Couldn't find any domain render attempts 🤷
	</BaseAlert>
	<div v-else class="rounded-lg bg-white p-4 dark:bg-gray-900">
		<SortableTable
			title="Latest Domain Render Attempts"
			show-title
			:items="domainRenderAttempts"
			:headers="[
				{
					text: 'Domain',
					value: 'domain',
					sortable: true,
				},
				{
					text: 'Time',
					value: 'lastRenderTime',
					sortable: true,
				},
				{
					text: 'Status',
					value: 'lastRenderSuccess',
				},
			]"
			initial-sort-by="lastRenderTime"
			sort-desc
			item-unique-key="id"
			expand-all
		>
			<template #item.lastRenderTime="{ lastRenderTime }">{{
				lastRenderTime ? format(lastRenderTime, 'Pp') : 'N/A'
			}}</template>
			<template #item.lastRenderSuccess="{ lastRenderSuccess }">
				<span
					class="rounded-md border px-2 py-1 font-medium"
					:class="
						lastRenderSuccess
							? 'border-green-300 bg-green-100 text-green-900 dark:border-green-500 dark:bg-green-950 dark:text-green-300'
							: 'border-red-200 bg-red-100 text-red-800 dark:border-red-500 dark:bg-red-950 dark:text-red-300'
					"
				>
					{{
						lastRenderSuccess === null
							? 'N/A'
							: lastRenderSuccess
								? 'Succeeded'
								: 'Failed'
					}}
				</span>
			</template>
			<template #item_expanded="domain">
				<div
					v-for="render in domain.renders"
					:key="render.render_time"
					class="m-2 rounded-xl bg-white p-4 dark:bg-gray-800"
				>
					<span
						class="-mt-2 mb-2 block text-lg font-semibold text-gray-800 dark:text-gray-200"
					>
						Last {{ render.success ? 'Successful' : 'Failed' }} Render
					</span>
					<dl class="grid gap-2">
						<div>
							<dt
								class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
							>
								Time
							</dt>
							<dd>
								<time :datetime="render.render_time">
									{{ format(render.render_time, 'PPpp') }}
								</time>
							</dd>
						</div>
						<div>
							<dt
								class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
							>
								Trigger
							</dt>
							<dd>
								{{ snakeToTitleCase(render.trigger) }}
							</dd>
						</div>
						<div>
							<dt
								class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
							>
								Schema Errors
							</dt>
							<dd class="mt-1">
								<JsonViewer
									:json-data="render.schema_errors"
									key-name="schema_errors"
								/>
							</dd>
						</div>
						<div>
							<dt
								class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
							>
								JSON Response
							</dt>
							<dd class="mt-1">
								<JsonViewer :json-data="render.response" key-name="response" />
							</dd>
						</div>
					</dl>
				</div>
			</template>
		</SortableTable>
	</div>
	<LoadingSpinner
		overlay
		:loading="loading"
		text="Getting domain render attempts..."
		bg-class="bg-gray-900 text-white"
	/>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { format } from 'date-fns';

import useDomainStore from '@/stores/agent/agent-domains';

import BaseAlert from '@/components/ui/BaseAlert.vue';
import JsonViewer from '@/components/ui/JsonViewer.vue';
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue';
import SortableTable from '@/components/ui/SortableTable.vue';

const domainStore = useDomainStore();
const { domains, domainRenderAttempts } = storeToRefs(domainStore);
const loading = ref(false);

function snakeToTitleCase(str) {
	return str
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

onMounted(async () => {
	loading.value = true;
	await domainStore.getDomainRenderAttempts();
	loading.value = false;
});
</script>
